import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Link } from "@tanstack/react-router";
import { PRODUCTS } from "@/features/products/catalog";
import { BrandLockup } from "./BrandLockup";

export const FOOTER_CONTACT_HREF = "mailto:support@sylviq.io";

const LEGAL_LINKS = [
  { label: "Privacy", to: "/legal/privacy" },
  { label: "Terms", to: "/legal/terms" },
  { label: "Cookies", to: "/legal/cookies" },
  { label: "Acceptable use", to: "/legal/acceptable-use" },
  { label: "Subprocessors", to: "/legal/subprocessors" },
] as const;

const linkClass =
  "rounded text-sm text-[var(--muted-foreground)] underline-offset-4 transition hover:text-[var(--foreground)] hover:underline";

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-xs font-semibold tracking-widest text-[var(--foreground)] uppercase">
        {title}
      </h2>
      <ul className="mt-4 grid list-none gap-2.5 p-0">{children}</ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-[var(--border)] bg-[var(--surface)]">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_2fr]">
          <div className="max-w-sm">
            <BrandLockup className="h-14 w-14" />
            <p className="mt-4 text-sm text-pretty text-[var(--muted-foreground)]">
              One account for your whole back office. Accounting is live
              today; inventory, CRM and more join the same organization as they
              ship.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="sm" className="rounded-full">
                <Link to="/signup">Get started</Link>
              </Button>
              <Button
                asChild
                size="sm"
                variant="outline"
                className="rounded-full"
              >
                <a href="/#early-access">Join the waitlist</a>
              </Button>
            </div>
          </div>

          <nav
            aria-label="Footer"
            className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3"
          >
            <FooterColumn title="Products">
              {PRODUCTS.map((product) => (
                <li key={product.slug}>
                  <Link
                    to="/products/$slug"
                    params={{ slug: product.slug }}
                    className={linkClass}
                  >
                    {product.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/products" className={linkClass}>
                  All products
                </Link>
              </li>
            </FooterColumn>
            <FooterColumn title="Company">
              <li>
                <Link to="/pricing" className={linkClass}>
                  Pricing
                </Link>
              </li>
              <li>
                <a href="/#how-it-works" className={linkClass}>
                  How it works
                </a>
              </li>
              <li>
                <a href="https://docs.sylviq.io/docs" className={linkClass}>
                  Documentation
                </a>
              </li>
              <li>
                <Link to="/support" className={linkClass}>
                  Support
                </Link>
              </li>
              <li>
                <Link to="/security" className={linkClass}>
                  Security
                </Link>
              </li>
              <li>
                <a href={FOOTER_CONTACT_HREF} className={linkClass}>
                  Contact us
                </a>
              </li>
              <li>
                <Link to="/login" className={linkClass}>
                  Sign in
                </Link>
              </li>
            </FooterColumn>
            <FooterColumn title="Legal">
              {LEGAL_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </FooterColumn>
          </nav>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-2 border-t border-[var(--border)] pt-6 text-xs text-[var(--muted-foreground)] sm:flex-row sm:items-center">
          <span>© {new Date().getFullYear()} SYLVIQ. All rights reserved.</span>
          <span>
            Accounting is live today. Inventory, CRM and more are in development.
          </span>
        </div>
      </div>
    </footer>
  );
}
